import { useMemo, useSyncExternalStore } from 'react';
import en from './locales/en';
import { DEFAULT_LANGUAGE, intlLocaleFor, isLanguage, Language } from './languages';
import type { ListKey, Messages, TermGroup, TranslationKey, TranslationVars } from './types';

const STORAGE_KEY = 'whatzneu.language';

const loaders: Record<Exclude<Language, 'en'>, () => Promise<Messages>> = {
  ta: () => import(/* webpackChunkName: "locale-ta" */ './locales/ta').then(module => module.default),
  hi: () => import(/* webpackChunkName: "locale-hi" */ './locales/hi').then(module => module.default),
  te: () => import(/* webpackChunkName: "locale-te" */ './locales/te').then(module => module.default),
};

const cache: Partial<Record<Language, Messages>> = { en };

interface I18nState {
  language: Language;
  messages: Messages;
}

let state: I18nState = { language: DEFAULT_LANGUAGE, messages: en };
let requested: Language = DEFAULT_LANGUAGE;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function lookup(messages: Messages, key: string): unknown {
  return key.split('.').reduce<unknown>(
    (node, part) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined),
    messages,
  );
}

function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

function humanize(value: string): string {
  const cleaned = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function translateWith(messages: Messages, key: TranslationKey, vars?: TranslationVars): string {
  const value = lookup(messages, key) ?? lookup(en, key);
  return typeof value === 'string' ? interpolate(value, vars) : key;
}

function listWith(messages: Messages, key: ListKey): string[] {
  const value = lookup(messages, key) ?? lookup(en, key);
  return Array.isArray(value) ? (value as string[]) : [];
}

/** Translates a backend enum such as a status; unknown values are humanized rather than hidden. */
function termWith(messages: Messages, group: TermGroup, value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const code = value.trim().toUpperCase();
  const terms = messages.common.terms[group] as Record<string, string>;
  const english = en.common.terms[group] as Record<string, string>;
  return terms[code] ?? english[code] ?? humanize(value);
}

export async function setLanguage(language: Language): Promise<void> {
  requested = language;
  let messages = cache[language];
  if (!messages) {
    try {
      messages = await loaders[language as Exclude<Language, 'en'>]();
      cache[language] = messages;
    } catch {
      return;
    }
  }
  if (requested !== language) return;
  state = { language, messages };
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage can be unavailable in private browsing; the choice then lasts for this visit only.
  }
  document.documentElement.lang = language;
  listeners.forEach(listener => listener());
}

export function currentLanguage(): Language {
  return state.language;
}

export function currentIntlLocale(): string {
  return intlLocaleFor(state.language);
}

/** For code outside React components, such as API error helpers. */
export function translate(key: TranslationKey, vars?: TranslationVars): string {
  return translateWith(state.messages, key, vars);
}

export function useTranslation() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  return useMemo(
    () => ({
      language: snapshot.language,
      intlLocale: intlLocaleFor(snapshot.language),
      t: (key: TranslationKey, vars?: TranslationVars) => translateWith(snapshot.messages, key, vars),
      tList: (key: ListKey) => listWith(snapshot.messages, key),
      term: (group: TermGroup, value: string | undefined, fallback = '') => termWith(snapshot.messages, group, value, fallback),
      setLanguage,
    }),
    [snapshot],
  );
}

function storedLanguage(): Language {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

const initialLanguage = storedLanguage();

/** Resolves once the saved language is loaded, so the first paint is not briefly in English. */
export const i18nReady: Promise<void> =
  initialLanguage === DEFAULT_LANGUAGE ? Promise.resolve() : setLanguage(initialLanguage);
